import React, { useState, useRef, useMemo } from 'react';
import { 
  ChevronLeft, Edit2, X, Plus, Save, Trash2,
  TrendingDown, Search, Calendar, Fuel, ShoppingBag, 
  Music2, UtensilsCrossed, Hotel, Megaphone, CreditCard, 
  Package, MoreHorizontal, Receipt, PieChart, ArrowUpRight,
  ArrowDownRight, ChevronRight, BarChart3, Wallet
} from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { CardSkeleton } from '../common/Skeleton';
import EmptyState from '../common/EmptyState';
import ConfirmModal from '../common/ConfirmModal';
import toast from 'react-hot-toast';
import { AddExpenseGate } from '../subscription/FeatureGates';

const Expenses = ({
  generalExpenses,
  onNavigate,
  onAddExpense,
  onUpdateExpense,
  onDeleteExpense,
  loading
}) => {
  const expenseCategories = [
    { id: 'Van/Gas', label: 'Van/Gas', icon: Fuel, color: 'sky', emoji: '🚐' },
    { id: 'Merch', label: 'Merch', icon: ShoppingBag, color: 'violet', emoji: '👕' },
    { id: 'Equipment', label: 'Equipment', icon: Music2, color: 'rose', emoji: '🎸' },
    { id: 'Food', label: 'Food', icon: UtensilsCrossed, color: 'emerald', emoji: '🍔' },
    { id: 'Lodging', label: 'Lodging', icon: Hotel, color: 'amber', emoji: '🏨' },
    { id: 'Marketing', label: 'Marketing', icon: Megaphone, color: 'fuchsia', emoji: '📱' },
    { id: 'Fees', label: 'Fees', icon: CreditCard, color: 'cyan', emoji: '💳' },
    { id: 'Other', label: 'Other', icon: Package, color: 'slate', emoji: '📦' }
  ];

  const [expenseForm, setExpenseForm] = useState({
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    category: 'Van/Gas'
  });
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filterCategory, setFilterCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [activeTab, setActiveTab] = useState('transactions');

  const getCategoryConfig = (categoryId) => {
    return expenseCategories.find(c => c.id === categoryId) || expenseCategories[7];
  };

  const getColorClasses = (color) => {
    const colors = {
      sky: { bg: 'bg-sky-50 dark:bg-sky-950/40', text: 'text-sky-600 dark:text-sky-400', border: 'border-sky-200 dark:border-sky-800', gradient: 'from-sky-400 to-sky-600', bar: 'bg-sky-500' },
      violet: { bg: 'bg-violet-50 dark:bg-violet-950/40', text: 'text-violet-600 dark:text-violet-400', border: 'border-violet-200 dark:border-violet-800', gradient: 'from-violet-400 to-violet-600', bar: 'bg-violet-500' },
      rose: { bg: 'bg-rose-50 dark:bg-rose-950/40', text: 'text-rose-600 dark:text-rose-400', border: 'border-rose-200 dark:border-rose-800', gradient: 'from-rose-400 to-rose-600', bar: 'bg-rose-500' },
      emerald: { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800', gradient: 'from-emerald-400 to-emerald-600', bar: 'bg-emerald-500' },
      amber: { bg: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800', gradient: 'from-amber-400 to-amber-600', bar: 'bg-amber-500' },
      fuchsia: { bg: 'bg-fuchsia-50 dark:bg-fuchsia-950/40', text: 'text-fuchsia-600 dark:text-fuchsia-400', border: 'border-fuchsia-200 dark:border-fuchsia-800', gradient: 'from-fuchsia-400 to-fuchsia-600', bar: 'bg-fuchsia-500' },
      cyan: { bg: 'bg-cyan-50 dark:bg-cyan-950/40', text: 'text-cyan-600 dark:text-cyan-400', border: 'border-cyan-200 dark:border-cyan-800', gradient: 'from-cyan-400 to-cyan-600', bar: 'bg-cyan-500' },
      slate: { bg: 'bg-slate-100 dark:bg-slate-800/40', text: 'text-slate-600 dark:text-slate-400', border: 'border-slate-200 dark:border-slate-700', gradient: 'from-slate-400 to-slate-600', bar: 'bg-slate-500' }
    };
    return colors[color] || colors.slate;
  };

  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

    const thisMonthExpenses = generalExpenses.filter(e => {
      const d = new Date(e.date);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    });

    const lastMonthExpenses = generalExpenses.filter(e => {
      const d = new Date(e.date);
      return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
    });

    const thisMonthTotal = thisMonthExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
    const lastMonthTotal = lastMonthExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
    const totalExpenses = generalExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

    const percentChange = lastMonthTotal > 0 
      ? ((thisMonthTotal - lastMonthTotal) / lastMonthTotal) * 100 
      : thisMonthTotal > 0 ? 100 : 0;

    const categoryBreakdown = expenseCategories.map(cat => ({
      ...cat,
      total: generalExpenses.filter(e => e.category === cat.id).reduce((sum, e) => sum + parseFloat(e.amount || 0), 0),
      count: generalExpenses.filter(e => e.category === cat.id).length,
    })).filter(cat => cat.count > 0).sort((a, b) => b.total - a.total);

    const weeklyData = [];
    for (let i = 3; i >= 0; i--) {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - (i * 7) - now.getDay());
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 7);

      const weekTotal = generalExpenses.filter(e => {
        const d = new Date(e.date);
        return d >= weekStart && d < weekEnd;
      }).reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

      weeklyData.push({
        label: i === 0 ? 'This Week' : i === 1 ? 'Last Week' : `${i} weeks ago`,
        total: weekTotal
      });
    }

    const maxWeekly = Math.max(...weeklyData.map(w => w.total), 1);
    const avgPerTransaction = generalExpenses.length > 0 ? totalExpenses / generalExpenses.length : 0;
    const topCategory = categoryBreakdown[0] || null;

    return { totalExpenses, thisMonthTotal, lastMonthTotal, percentChange, categoryBreakdown, weeklyData, maxWeekly, avgPerTransaction, topCategory, transactionCount: generalExpenses.length };
  }, [generalExpenses]);

  const handleAddExpense = () => {
    if (!expenseForm.description || !expenseForm.amount || !expenseForm.date) {
      toast.error('Please fill in all fields');
      return;
    }

    if (editingExpenseId) {
      onUpdateExpense(editingExpenseId, expenseForm.description, expenseForm.amount, expenseForm.date, expenseForm.category);
      setEditingExpenseId(null);
      toast.success('Expense updated');
    } else {
      onAddExpense(expenseForm.description, expenseForm.amount, expenseForm.date, expenseForm.category);
      toast.success('Expense added');
    }

    setExpenseForm({ description: '', amount: '', date: new Date().toISOString().split('T')[0], category: 'Van/Gas' });
    setShowAddForm(false);
  };

  const handleEditExpense = (expense) => {
    setExpenseForm({ description: expense.description, amount: expense.amount, date: expense.date, category: expense.category || 'Other' });
    setEditingExpenseId(expense.id);
    setShowAddForm(true);
    setSelectedExpense(null);
  };

  const handleCancelEdit = () => {
    setExpenseForm({ description: '', amount: '', date: new Date().toISOString().split('T')[0], category: 'Van/Gas' });
    setEditingExpenseId(null);
    setShowAddForm(false);
  };

  const filteredExpenses = generalExpenses
    .filter(exp => {
      const matchesCategory = filterCategory === 'All' || exp.category === filterCategory;
      const matchesSearch = !searchTerm || exp.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const groupedByDate = filteredExpenses.reduce((groups, expense) => {
    const date = expense.date;
    if (!groups[date]) groups[date] = [];
    groups[date].push(expense);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4">
        <div className="max-w-6xl mx-auto space-y-4">
          <CardSkeleton /><CardSkeleton /><CardSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-24 md:pb-8">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between px-4 py-3">
          <button 
            onClick={() => onNavigate('dashboard')} 
            className="p-2 rounded-full bg-gray-100 dark:bg-gray-700"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">Expenses</h1>
          <AddExpenseGate currentCount={generalExpenses.length}>
            <button 
              onClick={() => setShowAddForm(true)} 
              className="p-2 rounded-full bg-purple-600 text-white"
            >
              <Plus size={20} />
            </button>
          </AddExpenseGate>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:block max-w-6xl mx-auto px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-800 dark:text-gray-100">Expenses</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Track your spending</p>
            </div>
            <div className="flex items-center gap-2">
              <AddExpenseGate currentCount={generalExpenses.length}>
                <button onClick={() => setShowAddForm(true)} className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors">
                  <Plus size={20} />
                  Add Expense
                </button>
              </AddExpenseGate>
              <button 
                onClick={() => onNavigate('dashboard')} 
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="max-w-6xl mx-auto px-4 lg:px-8 pb-4">
          <div className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl max-w-md">
            <button onClick={() => setActiveTab('transactions')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-medium text-sm transition-all ${activeTab === 'transactions' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-600 dark:text-slate-400'}`}>
              <Receipt size={18} />Transactions
            </button>
            <button onClick={() => setActiveTab('summary')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-medium text-sm transition-all ${activeTab === 'summary' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm' : 'text-slate-600 dark:text-slate-400'}`}>
              <PieChart size={18} />Summary
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6">
        {/* SUMMARY TAB */}
        {activeTab === 'summary' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="col-span-2 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 text-white shadow-xl">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-slate-400 text-sm font-medium">Total Expenses</p>
                    <p className="text-3xl lg:text-4xl font-bold mt-1">{formatCurrency(stats.totalExpenses)}</p>
                    <p className="text-slate-400 text-sm mt-2">{stats.transactionCount} transaction{stats.transactionCount !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="p-3 bg-white/10 rounded-xl"><Wallet size={28} /></div>
                </div>
              </div>
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-500 text-sm">This Month</span>
                  <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${stats.percentChange > 0 ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-600' : stats.percentChange < 0 ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' : 'bg-slate-100 text-slate-600'}`}>
                    {stats.percentChange > 0 ? <ArrowUpRight size={14} /> : stats.percentChange < 0 ? <ArrowDownRight size={14} /> : null}
                    {Math.abs(stats.percentChange).toFixed(0)}%
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{formatCurrency(stats.thisMonthTotal)}</p>
              </div>
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-500 text-sm">Avg/Transaction</span>
                  <BarChart3 size={18} className="text-slate-400" />
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{formatCurrency(stats.avgPerTransaction)}</p>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">Weekly Spending</h3>
                    <TrendingDown size={18} className="text-slate-400" />
                  </div>
                  <div className="flex items-end gap-3 h-40">
                    {stats.weeklyData.map((week, idx) => (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                        <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden" style={{ height: '100px' }}>
                          <div className="w-full bg-gradient-to-t from-violet-500 to-fuchsia-500 rounded-lg transition-all duration-500" style={{ height: `${(week.total / stats.maxWeekly) * 100}%`, marginTop: `${100 - (week.total / stats.maxWeekly) * 100}%` }} />
                        </div>
                        <span className="text-xs text-slate-500">{week.label.split(' ')[0]}</span>
                        <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{formatCurrency(week.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {stats.topCategory && (
                  <div className={`rounded-2xl p-5 border-2 ${getColorClasses(stats.topCategory.color).border} ${getColorClasses(stats.topCategory.color).bg}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-3xl">{stats.topCategory.emoji}</span>
                      <div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Top Spending Category</p>
                        <p className={`font-bold text-lg ${getColorClasses(stats.topCategory.color).text}`}>{stats.topCategory.label}</p>
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{formatCurrency(stats.topCategory.total)}</p>
                    <p className="text-sm text-slate-500 mt-1">{((stats.topCategory.total / stats.totalExpenses) * 100).toFixed(0)}% of total</p>
                  </div>
                )}
              </div>

              <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100">By Category</h3>
                  <PieChart size={18} className="text-slate-400" />
                </div>
                {stats.categoryBreakdown.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-8">No expenses recorded yet</p>
                ) : (
                  <div className="space-y-4">
                    {stats.categoryBreakdown.map(cat => {
                      const colors = getColorClasses(cat.color);
                      const percentage = stats.totalExpenses > 0 ? (cat.total / stats.totalExpenses) * 100 : 0;
                      const IconComponent = cat.icon;
                      return (
                        <div key={cat.id} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${colors.bg}`}><IconComponent size={16} className={colors.text} /></div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-slate-100 text-sm">{cat.label}</p>
                                <p className="text-xs text-slate-500">{cat.count} transaction{cat.count !== 1 ? 's' : ''}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-slate-900 dark:text-slate-100">{formatCurrency(cat.total)}</p>
                              <p className="text-xs text-slate-500">{percentage.toFixed(1)}%</p>
                            </div>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className={`h-full ${colors.bar} rounded-full transition-all duration-500`} style={{ width: `${percentage}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <button onClick={() => setActiveTab('transactions')} className="flex items-center gap-2 py-3 px-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              <Receipt size={18} />View All Transactions<ChevronRight size={18} />
            </button>
          </div>
        )}

        {/* TRANSACTIONS TAB */}
        {activeTab === 'transactions' && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Desktop Sidebar */}
            <div className="hidden lg:block space-y-4">
              <div className="bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-2xl p-5 text-white">
                <p className="text-violet-100 text-sm">Total Expenses</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(stats.totalExpenses)}</p>
                <div className="flex gap-4 mt-4 pt-4 border-t border-white/20">
                  <div><p className="text-violet-100 text-xs">This Month</p><p className="text-lg font-semibold">{formatCurrency(stats.thisMonthTotal)}</p></div>
                  <div><p className="text-violet-100 text-xs">Transactions</p><p className="text-lg font-semibold">{stats.transactionCount}</p></div>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
                <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Categories</h3>
                <div className="space-y-1">
                  <button onClick={() => setFilterCategory('All')} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl font-medium text-sm transition-all ${filterCategory === 'All' ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900' : 'text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                    <span>All Categories</span><span className="text-xs opacity-60">{stats.transactionCount}</span>
                  </button>
                  {expenseCategories.map(cat => {
                    const colors = getColorClasses(cat.color);
                    const count = generalExpenses.filter(e => e.category === cat.id).length;
                    if (count === 0) return null;
                    return (
                      <button key={cat.id} onClick={() => setFilterCategory(cat.id)} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl font-medium text-sm transition-all ${filterCategory === cat.id ? `bg-gradient-to-r ${colors.gradient} text-white` : 'text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                        <div className="flex items-center gap-2"><span>{cat.emoji}</span><span>{cat.label}</span></div>
                        <span className="text-xs opacity-60">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Mobile Stats */}
            <div className="lg:hidden flex gap-3 overflow-x-auto pb-2 -mx-4 px-4">
              <div className="flex-shrink-0 bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-xl px-4 py-3 text-white min-w-[140px]">
                <p className="text-violet-100 text-xs">Total</p>
                <p className="text-xl font-bold">{formatCurrency(stats.totalExpenses)}</p>
              </div>
              <div className="flex-shrink-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 min-w-[140px]">
                <p className="text-slate-500 text-xs">This Month</p>
                <p className="text-xl font-bold text-slate-900 dark:text-slate-100">{formatCurrency(stats.thisMonthTotal)}</p>
              </div>
            </div>

            {/* Mobile Category Pills */}
            <div className="lg:hidden flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
              <button onClick={() => setFilterCategory('All')} className={`flex-shrink-0 px-4 py-2 rounded-xl font-medium text-sm ${filterCategory === 'All' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>All</button>
              {stats.categoryBreakdown.map(cat => {
                const colors = getColorClasses(cat.color);
                return (
                  <button key={cat.id} onClick={() => setFilterCategory(cat.id)} className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm ${filterCategory === cat.id ? `bg-gradient-to-r ${colors.gradient} text-white` : `bg-white border border-slate-200 ${colors.text}`}`}>
                    <span>{cat.emoji}</span><span>{cat.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Expenses List */}
            <div className="lg:col-span-2 space-y-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search expenses..." className="w-full pl-12 pr-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 text-slate-900 dark:text-slate-100" />
                {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"><X size={18} /></button>}
              </div>

              {Object.keys(groupedByDate).length === 0 ? (
                filterCategory === 'All' && !searchTerm ? (
                  <AddExpenseGate currentCount={generalExpenses.length}>
                    <EmptyState 
                      icon={TrendingDown} 
                      title="No Expenses Yet" 
                      description="Start tracking your expenses." 
                      action={() => setShowAddForm(true)} 
                      actionLabel="Add Expense" 
                    />
                  </AddExpenseGate>
                ) : (
                  <EmptyState 
                    icon={TrendingDown} 
                    title="No Results" 
                    description="Try adjusting your filters." 
                    action={() => { setFilterCategory('All'); setSearchTerm(''); }} 
                    actionLabel="Clear Filters" 
                  />
                )
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedByDate).map(([date, expenses]) => (
                    <div key={date}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-500"><Calendar size={16} /><span>{formatDate(date)}</span></div>
                        <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{formatCurrency(expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0))}</span>
                      </div>
                      <div className="space-y-2">
                        {expenses.map(expense => {
                          const cat = getCategoryConfig(expense.category);
                          const colors = getColorClasses(cat.color);
                          const IconComponent = cat.icon;
                          return (
                            <div key={expense.id} className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-100 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 transition-all group">
                              <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-xl ${colors.bg}`}><IconComponent size={20} className={colors.text} /></div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-slate-900 dark:text-slate-100 truncate">{expense.description}</p>
                                  <p className={`text-sm ${colors.text}`}>{cat.label}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <p className="text-lg font-bold text-slate-900 dark:text-slate-100">-{formatCurrency(parseFloat(expense.amount))}</p>
                                  <div className="relative">
                                    <button onClick={() => setSelectedExpense(selectedExpense === expense.id ? null : expense.id)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg opacity-0 group-hover:opacity-100 lg:opacity-100 transition-all"><MoreHorizontal size={18} /></button>
                                    {selectedExpense === expense.id && (
                                      <>
                                        <div className="fixed inset-0 z-40" onClick={() => setSelectedExpense(null)} />
                                        <div className="absolute right-0 top-full mt-1 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-50 min-w-[140px]">
                                          <button onClick={() => handleEditExpense(expense)} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"><Edit2 size={16} />Edit</button>
                                          <button onClick={() => { setConfirmDelete(expense); setSelectedExpense(null); }} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"><Trash2 size={16} />Delete</button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleCancelEdit} />
          <div className="relative w-full sm:max-w-md lg:max-w-lg bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-auto">
            <div className="sm:hidden flex justify-center pt-3"><div className="w-10 h-1 bg-slate-300 dark:bg-slate-700 rounded-full" /></div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">{editingExpenseId ? 'Edit Expense' : 'New Expense'}</h2>
                <button onClick={handleCancelEdit} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"><X size={20} className="text-slate-500" /></button>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Category</label>
                <div className="grid grid-cols-4 gap-2">
                  {expenseCategories.map(cat => {
                    const colors = getColorClasses(cat.color);
                    const isSelected = expenseForm.category === cat.id;
                    return (
                      <button key={cat.id} onClick={() => setExpenseForm({ ...expenseForm, category: cat.id })} className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-all ${isSelected ? `bg-gradient-to-r ${colors.gradient} text-white shadow-lg scale-105` : `${colors.bg} ${colors.text} hover:scale-105`}`}>
                        <span className="text-xl">{cat.emoji}</span>
                        <span className="text-xs font-medium truncate w-full text-center">{cat.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Amount</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl font-medium">$</span>
                    <input type="number" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} placeholder="0.00" className="w-full pl-10 pr-4 py-4 text-2xl font-bold bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:border-violet-500 text-slate-900 dark:text-slate-100" autoFocus />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Date</label>
                  <input type="date" value={expenseForm.date} onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })} className="w-full px-4 py-4 text-lg bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:border-violet-500 text-slate-900 dark:text-slate-100" />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Description</label>
                <input type="text" value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} placeholder="What was this expense for?" className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:border-violet-500 text-slate-900 dark:text-slate-100" />
              </div>

              <div className="flex gap-3">
                <button onClick={handleCancelEdit} className="flex-1 py-3 px-4 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">Cancel</button>
                <button onClick={handleAddExpense} className="flex-1 py-3 px-4 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white rounded-xl font-medium transition-all shadow-lg shadow-violet-500/25 flex items-center justify-center gap-2">
                  {editingExpenseId ? <><Save size={20} />Update</> : <><Plus size={20} />Add Expense</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={() => { onDeleteExpense(confirmDelete.id); toast.success('Expense deleted'); setConfirmDelete(null); }} title="Delete Expense?" message={`Are you sure you want to delete "${confirmDelete?.description}" (${formatCurrency(parseFloat(confirmDelete?.amount || 0))})?`} danger={true} />
    </div>
  );
};

export default Expenses;